import { create } from 'zustand';

import type { AccountSettings } from '@shared/profile.js';
import type { ThemePrefs } from '../theme';

/** Initial prefs from OS media queries (used before user settings load / anonymous). */
function initialPrefs(): ThemePrefs {
  const prefersDark =
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const prefersReduced =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return {
    mode: prefersDark ? 'dark' : 'light',
    colorblind: 'off',
    font: 'default',
    reducedMotion: prefersReduced,
    custom: null,
  };
}

interface ThemeState {
  prefs: ThemePrefs;
  /** Partial live update (e.g., from AppearanceTab toggles). */
  setPrefs: (partial: Partial<ThemePrefs>) => void;
  /** Reconcile from persisted user settings after they load. */
  applySettings: (s: AccountSettings) => void;
}

/** Resolve `reduced_motion_preference` (auto follows the OS media query). */
function resolveReducedMotion(pref: AccountSettings['reduced_motion_preference']): boolean {
  if (pref === 'always_on') return true;
  if (pref === 'always_off') return false;
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const useThemeStore = create<ThemeState>((set) => ({
  prefs: initialPrefs(),
  setPrefs: (partial) => set((state) => ({ prefs: { ...state.prefs, ...partial } })),
  applySettings: (s) =>
    set((state) => ({
      prefs: {
        ...state.prefs,
        mode: s.theme_mode,
        colorblind: s.colorblind_mode,
        font: s.font_preference,
        reducedMotion: resolveReducedMotion(s.reduced_motion_preference),
      },
    })),
}));
